import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { UserLabelsTable } from "./-components/user-labels-table";
import { DashboardLayout } from "@/components/dashboard-layout";
import { useQuery } from "@tanstack/react-query";
import { organisationsQueryOptions } from "@/server/organisations";
import { useContext, useMemo } from "react";
import { useState } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { useEffect } from "react";
import { useSearch } from "@tanstack/react-router";
import { UserLabelSidebar } from "./-components/user-label-sidebar";
import { userLabelsQueryOptions } from "@/server/user-labels";
import { closeFullscreenDialog } from "@/components/fullscreen-sidebar";
import { handleCloseParam } from "@/components/close-rightsidebar";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/user-labels")({
  component: () => (
    <DashboardLayout submenu={null}>
      <UserLabelsRouteComponent />
    </DashboardLayout>
  ),
  loader: async () => {
    return {
      crumb: "User labels",
    };
  },
});

function UserLabelsRouteComponent() {
  const navigate = useNavigate();
  const userLabelsQuery = useQuery(userLabelsQueryOptions());
  const userLabels = userLabelsQuery.data;
  const loadingUserLabels = userLabelsQuery.isLoading;
  const organisationsQuery = useQuery(organisationsQueryOptions());
  const organisations = organisationsQuery.data;
  const searchParams = useSearch({ strict: false }) as {
    key?: string;
  };

  const modifiedUserLabels = useMemo(
    () =>
      userLabels?.map((label) => ({
        ...label,
        organisations: label?.organisations?.map((organisation) => {
          const foundOrg = organisations?.find((o) => o.id.toString() === organisation);
          return foundOrg
            ? { value: foundOrg.id.toString(), label: foundOrg.name }
            : { value: label.key, label: label.key };
        }),
      })),
    [userLabels, organisations]
  );

  const [isContextReady, setIsContextReady] = useState(false);

  const context = useContext(DashboardContext);

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  // When the page loads with the sidebar open, the context might not be ready and the sidebar will thus not be set.
  // This effect ensures that the sidebar is set when the context is ready.
  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  useEffect(() => {
    if (!isContextReady || !context?.setRightSidebar) {
      return;
    }
    if (searchParams.key && modifiedUserLabels) {
      const label = modifiedUserLabels.find((label) => label.key?.toString() === searchParams.key);
      if (label) {
        context.setRightSidebar(<UserLabelSidebar labelKey={label.key} />);
        context.setRightSidebarParamKey?.("key");
      }
      if (searchParams.key === "new") {
        context.setRightSidebar(<UserLabelSidebar labelKey={null} />);
        context.setRightSidebarParamKey?.("key");
      }
    }
  }, [searchParams.key, isContextReady, modifiedUserLabels]);

  const closeSidebar = () => {
    if (context) {
      closeFullscreenDialog();
      handleCloseParam({
        paramToRemove: context.rightSidebarParamKey,
        setSidebar: context.setRightSidebar,
        navigate,
      });
    }
  };

  const handleAddLabel = () => {
    navigate({
      to: "/user-labels",
      replace: true,
      search: (prev) => ({ ...prev, key: "new" }),
    });
  };

  useEffect(() => {
    if (userLabelsQuery.isError) {
      const message =
        userLabelsQuery.error instanceof Error
          ? userLabelsQuery.error.message
          : "Failed to load user labels. Please try again later.";
      toast.error(message);
    }
  }, [userLabelsQuery.isError, userLabelsQuery.error]);

  return (
    <UserLabelsTable
      labels={modifiedUserLabels}
      handleAddLabel={handleAddLabel}
      loadingUserLabels={loadingUserLabels}
      closeSidebar={closeSidebar}
    />
  );
}
